import { useState, useEffect } from 'react';
import axios from 'axios';
import { ThumbsUp, ThumbsDown, Check, Send, X, ArrowUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_PREFIX = 'dividrip:howto-feedback:';

/**
 * HowToSectionFeedback — Round 91 (Feb 2026), revised in 91c.
 *
 * Tiny "Was this section helpful?" thumbs-up / thumbs-down strip
 * rendered at the end of every How-To section. POSTs to the public
 * `/api/how-to/feedback` endpoint (anonymous allowed; signed-in users
 * get upsert dedupe per section).
 *
 * 91c additions:
 *   • Text colors are darker by default so they read clearly on the
 *     How-To page's light/cream theme. `dark:` variants restore the
 *     softer violet/pastel palette for the TwylightCrow dark mode.
 *   • After a 👎 vote (Not yet), a small "Tell me more — what was
 *     unclear?" textarea slides in (280-char limit, character counter).
 *     The user can submit the comment, cancel out, or just leave it.
 *     A 👍 vote skips the textarea — users who liked it shouldn't have
 *     to think; we want zero friction on the positive path.
 */
export default function HowToSectionFeedback({ sectionId }) {
  const lsKey = LS_PREFIX + sectionId;
  const [current, setCurrent] = useState(() => {
    try { return localStorage.getItem(lsKey) || null; } catch { return null; }
  });
  const [submitting, setSubmitting] = useState(false);
  // 91c — when the user picks 👎, we show a comment textarea. State:
  //   showComment  → true after a Not-yet click, until the user
  //                  cancels (X) or submits the comment.
  //   comment      → textarea value.
  //   commentSaved → flips true after a successful comment POST so
  //                  we can show a thank-you confirmation instead of
  //                  the input.
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [commentSaved, setCommentSaved] = useState(false);

  // Keep state in sync if user changes vote in another tab.
  useEffect(() => {
    const onStorage = (e) => { if (e.key === lsKey) setCurrent(e.newValue || null); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [lsKey]);

  const send = async (rating) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/how-to/feedback`,
        { section_id: sectionId, rating },
        { withCredentials: true },
      );
      setCurrent(rating);
      try { localStorage.setItem(lsKey, rating); } catch { /* swallow */ }
      // 91c — open the comment box on Not-yet; close it on Yes (and
      // reset any in-flight comment-saved flag so a follow-up Not-yet
      // works cleanly).
      if (rating === 'down') {
        setShowComment(true);
        setCommentSaved(false);
      } else {
        setShowComment(false);
        setComment('');
        setCommentSaved(false);
      }
    } catch {
      // Silent — How-To is public and feedback is best-effort.
    } finally {
      setSubmitting(false);
    }
  };

  const sendComment = async () => {
    const trimmed = (comment || '').trim().slice(0, 280);
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/how-to/feedback`,
        { section_id: sectionId, rating: 'down', comment: trimmed },
        { withCredentials: true },
      );
      setCommentSaved(true);
      setComment('');
    } catch {
      // best-effort; surface silently
    } finally {
      setSubmitting(false);
    }
  };

  const Btn = ({ value, Icon, label, activeClass }) => {
    const isActive = current === value;
    return (
      <button
        type="button"
        onClick={() => send(value)}
        disabled={submitting}
        aria-pressed={isActive}
        title={`${label}${isActive ? ' (your current vote)' : ''}`}
        data-testid={`ht-feedback-${sectionId}-${value}`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors
          ${isActive
            ? activeClass
            : 'bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 dark:text-violet-300'}
          ${submitting ? 'opacity-60 cursor-wait' : ''}
        `}
      >
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
        {isActive && <Check className="w-3 h-3 opacity-80" />}
      </button>
    );
  };

  const charCount = (comment || '').length;
  const charsLeft = 280 - charCount;

  return (
    <div
      className="mt-6 pt-3 border-t border-violet-400/15 text-xs"
      data-testid={`ht-feedback-${sectionId}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-violet-800/85 dark:text-violet-300/70 mr-1 font-medium">Was this section helpful?</span>
        <Btn
          value="up"
          Icon={ThumbsUp}
          label="Yes"
          activeClass="bg-emerald-500/20 text-emerald-800 dark:text-emerald-300"
        />
        <Btn
          value="down"
          Icon={ThumbsDown}
          label="Not yet"
          activeClass="bg-rose-500/20 text-rose-800 dark:text-rose-300"
        />
        {current === 'up' && (
          <span className="text-violet-700/75 dark:text-violet-300/55 ml-1">Thanks — vote recorded.</span>
        )}
        {current === 'down' && !showComment && !commentSaved && (
          <button
            type="button"
            onClick={() => setShowComment(true)}
            className="ml-1 underline decoration-dotted text-violet-700/85 dark:text-violet-300/70 hover:text-violet-900 dark:hover:text-violet-200"
            data-testid={`ht-feedback-${sectionId}-reopen-comment`}
          >
            Tell me more →
          </button>
        )}
        {commentSaved && (
          <span className="text-emerald-700 dark:text-emerald-300 ml-1 font-medium">Got it — thanks for the detail.</span>
        )}
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
          className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:underline underline-offset-2 transition-colors"
          data-testid={`ht-back-to-top-${sectionId}`}
        >
          <ArrowUp className="w-3 h-3" />
          Back to top
        </button>
      </div>

      {/* 91c — Inline textarea, only shown after a Not-yet vote and
          only until the user submits or closes it. */}
      {current === 'down' && showComment && !commentSaved && (
        <div
          className="mt-2 flex items-start gap-2"
          data-testid={`ht-feedback-${sectionId}-comment-form`}
        >
          <div className="flex-1">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 280))}
              placeholder="What was unclear or missing in this section?"
              rows={2}
              maxLength={280}
              disabled={submitting}
              className="w-full px-2.5 py-1.5 rounded-md text-xs bg-violet-50/60 dark:bg-violet-950/30 border border-violet-300/40 dark:border-violet-400/15 text-violet-900 dark:text-violet-100 placeholder:text-violet-700/50 dark:placeholder:text-violet-300/40 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-none"
              data-testid={`ht-feedback-${sectionId}-comment-input`}
              autoFocus
            />
            <div className="flex items-center justify-between mt-1">
              <span className={`text-[10px] ${charsLeft < 30 ? 'text-amber-600 dark:text-amber-400' : 'text-violet-700/55 dark:text-violet-300/45'}`}>
                {charsLeft} characters left
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setShowComment(false); setComment(''); }}
                  disabled={submitting}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-violet-700/85 dark:text-violet-300/70 hover:text-violet-900 dark:hover:text-violet-200"
                  data-testid={`ht-feedback-${sectionId}-comment-cancel`}
                >
                  <X className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={sendComment}
                  disabled={submitting || !comment.trim()}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                  data-testid={`ht-feedback-${sectionId}-comment-send`}
                >
                  <Send className="w-3 h-3" />
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
